import { Fragment, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiChevronLeft, FiChevronRight, FiMapPin } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { TableLoader } from "../../Common/ShimmerLoader";
import { StatusBadge, formatRfidDate } from "./utils/statusBadge";
import RfidImage from "./utils/RfidImage";
import RfidHistoryTable from "./RfidHistoryTable";
import { getRfidRowId, getRfidHistoryKey } from "./utils/rfidHistoryUtils";
import { getRfidQrKey } from "./utils/rfidQrUtils";
import { useRfidStore } from "./store/useRfidStore";

const FIGMA = {
  navy: "#003366",
  orange: "#F5A623",
  textMuted: "#6B7280",
  expandBg: "#F1F5F9",
  subHeaderBg: "#DBEAFE",
  subHeaderText: "#003366",
  border: "#E0E0E0",
};

const PAGE_SIZE_OPTIONS = [100, 1000, 10000];

const MAIN_COLUMNS = [
  "S.No",
  "Image",
  "Status",
  "View On Map",
  "QR Code",
  "Asset Code",
  "Asset Type",
  "Asset Category",
  // "RFID Device",
  "IMEI/SL/MFG",
  "Zone",
  "Ward",
  "Location/Landmark",
  "Installed Area",
  "Other No",
  "Collection Type",
  "Vehicle Type",
  "Vehicle Capacity",
  "Owner Type",
  "Date Of Installation",
  "Warranty Expire Date",
  "Purchase Date",
  "Insurance No",
  "Insurance Validity",
  "Insurance Provider",
  "Installed By",
  "Last Modified On",
  "Actions",
];

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${FIGMA.navy}`,
  backgroundColor: FIGMA.navy,
  color: "#FFFFFF",
  padding: "10px",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center",
};

const tdStyle = {
  whiteSpace: "nowrap",
  border: `1px solid ${FIGMA.border}`,
  padding: "10px",
  textAlign: "center",
  color: "#374151",
  fontSize: "13px",
  backgroundColor: "#FFFFFF",
};

const statusThStyle = {
  ...thStyle,
  width: 90,
  minWidth: 90,
  maxWidth: 90,
  padding: "8px 6px",
};

const statusTdStyle = {
  ...tdStyle,
  width: 90,
  minWidth: 90,
  maxWidth: 90,
  padding: "6px 4px",
};

const expandTdStyle = {
  ...tdStyle,
  width: 36,
  minWidth: 36,
  maxWidth: 36,
  padding: "6px 4px",
};

const assetTypeThStyle = {
  ...thStyle,
  width: 162,
  minWidth: 162,
  maxWidth: 162,
};

const assetTypeTdStyle = {
  ...tdStyle,
  width: 162,
  minWidth: 162,
  maxWidth: 162,
};

const subThStyle = {
  whiteSpace: "nowrap",
  border: "1px solid #BFDBFE",
  padding: "10px",
  fontSize: "13px",
  fontWeight: 600,
  textAlign: "center",
  backgroundColor: FIGMA.subHeaderBg,
  color: FIGMA.subHeaderText,
};

const subTdStyle = {
  border: `1px solid ${FIGMA.border}`,
  padding: "10px",
  fontSize: "13px",
  textAlign: "center",
  color: "#374151",
  backgroundColor: "#FFFFFF",
  whiteSpace: "nowrap",
};

const subStatusThStyle = {
  ...subThStyle,
  width: 90,
  minWidth: 90,
  maxWidth: 90,
  padding: "8px 6px",
};

const subStatusTdStyle = {
  ...subTdStyle,
  width: 90,
  minWidth: 90,
  maxWidth: 90,
  padding: "6px 4px",
};

const ExpandButton = ({ isExpanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isExpanded ? "Collapse row" : "Expand row"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 18,
      height: 18,
      borderRadius: 3,
      border: `1px solid ${isExpanded ? FIGMA.orange : "#D1D5DB"}`,
      backgroundColor: "#FFFFFF",
      color: isExpanded ? FIGMA.orange : "#6B7280",
      fontSize: "12px",
      fontWeight: 500,
      cursor: "pointer",
      lineHeight: 1,
      padding: 0,
    }}
  >
    {isExpanded ? "−" : "+"}
  </button>
);

const RfidImageThumb = ({ imageUrl, onPreview }) => {
  const placeholder = (
    <div
      style={{
        width: 32,
        height: 32,
        background: "#F3F4F6",
        borderRadius: 4,
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <i className="fa-solid fa-microchip text-muted"></i>
    </div>
  );

  if (!imageUrl) return placeholder;

  return (
    <RfidImage
      imageUrl={imageUrl}
      onClick={() => onPreview?.(imageUrl)}
      style={{
        width: 32,
        height: 32,
        objectFit: "cover",
        borderRadius: 4,
        margin: "0 auto",
        display: "block",
        border: "1px solid #E5E7EB",
        cursor: "pointer",
      }}
      fallback={placeholder}
    />
  );
};

const RfidExpandableTable = ({
  rows,
  onEdit,
  onViewMap,
  onSelectQR,
  onViewQR,
  selectedQRs = [],
  loading = false,
}) => {
  const {
    fetchRfidHistory,
    rfidHistory,
    isHistoryLoading,
    pagination,
    setRfidPage,
    setRfidPageSize,
  } = useRfidStore();
  const [expandedIds, setExpandedIds] = useState([]);
  const [previewImage, setPreviewImage] = useState("");

  const { pageNumber, pageSize, totalRecords, totalPages } = pagination;
  const totalResults = totalRecords || rows?.length || 0;
  const currentPage = pageNumber;
  const rowsPerPage = pageSize;

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 6;
    for (let i = 1; i <= Math.min(totalPages, maxVisible); i += 1) {
      pages.push(i);
    }
    return pages;
  }, [totalPages]);

  const paginatedRows = rows ?? [];

  const rangeStart = totalResults ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const rangeEnd = Math.min(currentPage * rowsPerPage, totalResults);

  useEffect(() => {
    setExpandedIds([]);
  }, [rows]);

  const getRowId = (row) => getRfidHistoryKey(getRfidRowId(row));

  const toggleExpand = async (row) => {
    const rowId = getRowId(row);
    const isExpanded = expandedIds.includes(rowId);

    if (isExpanded) {
      setExpandedIds((prev) => prev.filter((id) => id !== rowId));
      return;
    }

    setExpandedIds((prev) => [...prev, rowId]);
    await fetchRfidHistory(rowId);
  };

  const handleViewMap = (row) => {
    if (!row || !onViewMap) return;
    const lat = Number(row?.latitude ?? row?.Latitude);
    const lng = Number(row?.longitude ?? row?.Longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    onViewMap(row);
  };

  if (loading) {
    return <TableLoader height={500} columns={10} />;
  }

  return (
    <div>
      <div
        style={{
          overflow: "hidden",
          borderRadius: 10,
          border: `1px solid ${FIGMA.border}`,
          backgroundColor: "#FFFFFF",
        }}
      >
        <div style={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
          <table
            style={{
              minWidth: 3000,
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              textAlign: "center",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    ...thStyle,
                    width: 36,
                    minWidth: 36,
                    maxWidth: 36,
                    padding: "8px 4px",
                    borderRight: "1px solid rgba(255,255,255,0.2)",
                  }}
                />
                {MAIN_COLUMNS.map((col, index) => (
                  <th
                    key={col}
                    style={{
                      ...(col === "Status"
                        ? statusThStyle
                        : col === "Asset Type"
                          ? assetTypeThStyle
                          : thStyle),
                      borderRight:
                        index < MAIN_COLUMNS.length - 1
                          ? "1px solid rgba(255,255,255,0.2)"
                          : "none",
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!paginatedRows?.length ? (
                <tr>
                  <td
                    colSpan={MAIN_COLUMNS.length + 1}
                    style={{ ...tdStyle, padding: "24px" }}
                  >
                    No data available
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, rowIndex) => {
                  const rowId = getRowId(row);
                  const serialNo = (currentPage - 1) * rowsPerPage + rowIndex + 1;
                  const isExpanded = expandedIds.includes(rowId);
                  const history = rfidHistory[rowId] || row.history || [];

                  return (
                    <Fragment key={rowId}>
                      <tr>
                        <td style={expandTdStyle}>
                          <ExpandButton
                            isExpanded={isExpanded}
                            onClick={() => toggleExpand(row)}
                          />
                        </td>
                        <td style={tdStyle}>{serialNo}</td>
                        <td style={tdStyle}>
                          <RfidImageThumb
                            imageUrl={row.imageUrl ?? row.imgUrl}
                            onPreview={setPreviewImage}
                          />
                        </td>
                        <td style={statusTdStyle}>
                          <StatusBadge isActive={row.isActive} compact />
                        </td>
                        <td style={tdStyle}>
                          {(() => {
                            const lat = Number(row?.latitude ?? row?.Latitude);
                            const lng = Number(row?.longitude ?? row?.Longitude);
                            const hasCoords =
                              !Number.isNaN(lat) && !Number.isNaN(lng);

                            return (
                              <button
                                type="button"
                                title={
                                  hasCoords
                                    ? "View on map"
                                    : "Coordinates not available"
                                }
                                disabled={!hasCoords}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!hasCoords) return;
                                  handleViewMap(row);
                                }}
                                style={{
                                  height: 28,
                                  width: 28,
                                  borderRadius: 6,
                                  border: "none",
                                  background: "#FFFFFF",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: hasCoords ? "#0284C7" : "#CBD5E1",
                                  cursor: hasCoords ? "pointer" : "not-allowed",
                                }}
                              >
                                <FiMapPin size={14} />
                              </button>
                            );
                          })()}
                        </td>
                        <td style={tdStyle}>
                          <div
                            className="d-flex gap-2 align-items-center"
                            style={{ justifyContent: "center" }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedQRs.includes(getRfidQrKey(row))}
                              onChange={() => onSelectQR?.(getRfidQrKey(row))}
                              disabled={!getRfidQrKey(row)}
                            />
                            <span
                              style={{
                                cursor: getRfidQrKey(row)
                                  ? "pointer"
                                  : "not-allowed",
                                textDecoration: "underline",
                                color: getRfidQrKey(row) ? "blue" : "#9CA3AF",
                              }}
                              onClick={() => {
                                if (!getRfidQrKey(row)) return;
                                onViewQR?.(row);
                              }}
                            >
                              View
                            </span>
                          </div>
                        </td>
                        <td style={tdStyle}>{row.rfidCode || "N/A"}</td>
                        <td style={assetTypeTdStyle}>{row.rfidTypeName || "N/A"}</td>
                        <td style={tdStyle}>{row.rfidCategoryName || "N/A"}</td>
                        {/* <td style={tdStyle}>{row.rfidDeviceName || "N/A"}</td> */}
                        <td style={tdStyle}>{row.imeiSlMfgNo || "N/A"}</td>
                        <td style={tdStyle}>{row.zoneName || "N/A"}</td>
                        <td style={tdStyle}>{row.wardNo || "N/A"}</td>
                        <td style={tdStyle}>{row.locationLandmark || "N/A"}</td>
                        <td style={tdStyle}>{row.installedArea || "N/A"}</td>
                        <td style={tdStyle}>{row.otherNo || "-"}</td>
                        <td style={tdStyle}>{row.collectionTypeName || "N/A"}</td>
                        <td style={tdStyle}>{row.vehicleTypeName || "N/A"}</td>
                        <td style={tdStyle}>{row.vehicleCapacityName || "N/A"}</td>
                        <td style={tdStyle}>{row.ownerTypeName || "N/A"}</td>
                        <td style={tdStyle}>
                          {formatRfidDate(row.dateOfInstallation)}
                        </td>
                        <td style={tdStyle}>
                          {formatRfidDate(row.warrantyExpireDate)}
                        </td>
                        <td style={tdStyle}>
                          {formatRfidDate(row.purchaseDate)}
                        </td>
                        <td style={tdStyle}>{row.insuranceNo || "N/A"}</td>
                        <td style={tdStyle}>
                          {formatRfidDate(row.insuranceValidity)}
                        </td>
                        <td style={tdStyle}>{row.insuranceProvider || "N/A"}</td>
                        <td style={tdStyle}>{row.installedBy || "N/A"}</td>
                        <td style={tdStyle}>
                          {formatRfidDate(row.lastModifiedOn)}
                        </td>
                        <td style={tdStyle}>
                          <button
                            type="button"
                            className="edit-swm"
                            onClick={() => onEdit(row)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr style={{ backgroundColor: FIGMA.expandBg }}>
                          <td
                            colSpan={MAIN_COLUMNS.length + 1}
                            style={{
                              border: `1px solid ${FIGMA.border}`,
                              padding: "16px 24px",
                            }}
                          >
                           
                            <RfidHistoryTable
                              history={history}
                              isLoading={isHistoryLoading}
                              subThStyle={subThStyle}
                              subTdStyle={subTdStyle}
                              subStatusThStyle={subStatusThStyle}
                              subStatusTdStyle={subStatusTdStyle}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <p style={{ margin: 0, fontSize: "14px", color: FIGMA.textMuted }}>
          Showing {rangeStart}-{rangeEnd} of {totalResults} results
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 20,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: "14px",
              color: FIGMA.textMuted,
              margin: 0,
            }}
          >
            Row per page
            <select
              value={rowsPerPage}
              onChange={(e) => setRfidPageSize(Number(e.target.value))}
              style={{
                appearance: "none",
                borderRadius: 6,
                border: `1px solid ${FIGMA.border}`,
                backgroundColor: "#F0F4F8",
                padding: "6px 28px 6px 12px",
                fontSize: "14px",
                color: "#374151",
                outline: "none",
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <nav
            style={{ display: "flex", alignItems: "center", gap: 6 }}
            aria-label="Pagination"
          >
            <button
              type="button"
              onClick={() => setRfidPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                opacity: currentPage === 1 ? 0.4 : 1,
              }}
              aria-label="Previous page"
            >
              <FiChevronLeft size={16} />
            </button>

            {pageNumbers.map((page) => (
              <button
                key={page}
                type="button"
                onClick={() => setRfidPage(page)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                  height: 32,
                  borderRadius: 6,
                  padding: "0 10px",
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  backgroundColor:
                    currentPage === page ? FIGMA.navy : "#F0F4F8",
                  color: currentPage === page ? "#FFFFFF" : "#374151",
                }}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setRfidPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 32,
                height: 32,
                border: "none",
                background: "transparent",
                color: "#9CA3AF",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                opacity: currentPage === totalPages ? 0.4 : 1,
              }}
              aria-label="Next page"
            >
              <FiChevronRight size={16} />
            </button>
          </nav>
        </div>
      </div>

      <Modal show={!!previewImage} onHide={() => setPreviewImage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <RfidImage
            imageUrl={previewImage}
            alt="Preview"
            style={{
              width: "100%",
              maxHeight: 400,
              objectFit: "contain",
              borderRadius: 5,
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RfidExpandableTable;
